import { useEffect, useState, useRef } from 'react';
import { Crown, Volume2, VolumeX, X, Sparkles } from 'lucide-react';

/**
 * GrandSovereignAvatar
 * - Persistent dashboard widget showing the Admiral's portrait + canonical message
 * - Auto-triggers a cinematic full-screen welcome modal on FIRST LOGIN only
 * - Optional Web Speech API voice playback (no external API needed)
 * - Typewriter reveal for high-fidelity text feel
 */
export default function GrandSovereignAvatar({ sovereign, juniorName, isFirstLogin, onAcknowledge }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [voiceAvailable] = useState(typeof window !== 'undefined' && 'speechSynthesis' in window);
  const utteranceRef = useRef(null);

  // Auto-open on first login
  useEffect(() => {
    if (isFirstLogin) {
      const t = setTimeout(() => setModalOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [isFirstLogin]);

  // Typewriter reveal when modal opens
  useEffect(() => {
    if (!modalOpen || !sovereign?.message) return;
    setTyped('');
    let i = 0;
    const msg = sovereign.message;
    const interval = setInterval(() => {
      i += 1;
      setTyped(msg.slice(0, i));
      if (i >= msg.length) clearInterval(interval);
    }, 28);
    return () => clearInterval(interval);
  }, [modalOpen, sovereign?.message]);

  const speak = () => {
    if (!voiceAvailable || !sovereign?.message) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(sovereign.message);
    // Fatherly, commanding tone: slower rate, lower pitch
    u.rate = 0.88;
    u.pitch = 0.78;
    u.volume = 1.0;
    // Try to pick a deep male English voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) =>
      /uk|gb|english|en-/i.test(v.lang) && /male|daniel|alex|fred|david|james|rishi|aaron|arthur/i.test(v.name.toLowerCase())
    ) || voices.find((v) => /en-gb|en-us/i.test(v.lang)) || voices[0];
    if (preferred) u.voice = preferred;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utteranceRef.current = u;
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const stopSpeak = () => {
    if (!voiceAvailable) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const acknowledge = async () => {
    stopSpeak();
    setModalOpen(false);
    if (isFirstLogin && onAcknowledge) await onAcknowledge();
  };

  if (!sovereign) return null;

  return (
    <>
      {/* Persistent dashboard widget */}
      <div data-testid="grand-sovereign-widget" className="sov-border bg-[#0A0A0A] p-6 mb-8 flex flex-col md:flex-row gap-6 items-start">
        <div className="relative flex-shrink-0">
          <div
            className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-[#D4AF37] bg-cover bg-center shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            style={{ backgroundImage: `url(${sovereign.avatar_url})` }}
            data-testid="grand-sovereign-avatar"
          />
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center border-2 border-[#050505]">
            <Crown size={12} strokeWidth={2} className="text-[#050505]" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-1">{sovereign.title}</div>
          <div className="font-display text-2xl text-[#F5F5F5] mb-3">{sovereign.name}</div>
          <p className="text-sm text-[#A3A3A3] font-light leading-relaxed italic">"{sovereign.message}"</p>
          <div className="flex items-center gap-3 mt-4">
            <button
              data-testid="grand-sovereign-replay"
              onClick={() => setModalOpen(true)}
              className="text-[10px] tracking-[0.3em] uppercase border border-[#D4AF37] text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors flex items-center gap-2"
            >
              <Sparkles size={11} strokeWidth={1.5} /> Replay Message
            </button>
            {voiceAvailable && (
              <button
                data-testid="grand-sovereign-voice"
                onClick={speaking ? stopSpeak : speak}
                className="text-[10px] tracking-[0.3em] uppercase border border-[#444] text-[#A3A3A3] px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-2"
              >
                {speaking ? <VolumeX size={11} strokeWidth={1.5} /> : <Volume2 size={11} strokeWidth={1.5} />}
                {speaking ? 'Stop' : 'Hear Voice'}
              </button>
            )}
          </div>
        </div>
        <div className="text-right text-[10px] tracking-[0.3em] uppercase text-[#666] flex-shrink-0">
          <div>Captain: {juniorName}</div>
        </div>
      </div>

      {/* Cinematic first-login modal */}
      {modalOpen && (
        <div
          data-testid="grand-sovereign-modal"
          className="fixed inset-0 z-[100] bg-[#050505]/95 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in"
          role="dialog"
        >
          <button
            onClick={acknowledge}
            data-testid="grand-sovereign-modal-close"
            className="absolute top-6 right-6 p-2 border border-[#444] text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>

          <div className="max-w-3xl w-full">
            {/* Cinematic avatar */}
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-8">
                {/* Halo glow */}
                <div className="absolute inset-0 -m-8 rounded-full bg-[#D4AF37]/10 blur-3xl animate-pulse" />
                <div
                  className="relative w-40 h-40 md:w-52 md:h-52 rounded-full border-4 border-[#D4AF37] bg-cover bg-center shadow-[0_0_60px_rgba(212,175,55,0.4)]"
                  style={{ backgroundImage: `url(${sovereign.avatar_url})` }}
                />
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#D4AF37] text-[#050505] text-[9px] tracking-[0.4em] uppercase flex items-center gap-2 whitespace-nowrap">
                  <Crown size={11} strokeWidth={2} /> Grand Sovereign
                </div>
              </div>

              <div className="text-[10px] tracking-[0.5em] uppercase text-[#D4AF37] mb-3">A message has arrived from the fleet</div>
              <h2 className="text-3xl md:text-5xl font-extralight tracking-tight mb-2">
                {sovereign.name}
              </h2>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-10">{sovereign.title}</div>

              {/* Typewriter message */}
              <div className="max-w-2xl">
                <p className="font-display text-xl md:text-2xl text-[#F5F5F5] leading-relaxed italic min-h-[6em]">
                  "{typed}<span className="inline-block w-0.5 h-5 bg-[#D4AF37] ml-1 animate-pulse align-middle" />"
                </p>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mt-6">{sovereign.signature}</div>
              </div>

              <div className="flex items-center gap-4 mt-10">
                {voiceAvailable && (
                  <button
                    data-testid="grand-sovereign-modal-voice"
                    onClick={speaking ? stopSpeak : speak}
                    className="border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase px-5 py-3 hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2"
                  >
                    {speaking ? <VolumeX size={12} strokeWidth={1.5} /> : <Volume2 size={12} strokeWidth={1.5} />}
                    {speaking ? 'Stop Voice' : 'Hear His Voice'}
                  </button>
                )}
                <button
                  data-testid="grand-sovereign-acknowledge"
                  onClick={acknowledge}
                  className="bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium px-8 py-3 hover:bg-[#E5C158] transition-colors flex items-center gap-2"
                >
                  <Crown size={12} strokeWidth={2} /> {isFirstLogin ? 'Aye, Sovereign — Board the Bridge' : 'Close'}
                </button>
              </div>
              {isFirstLogin && (
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#666] mt-4">+10 XP on acknowledgement</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
